import { get, pick } from "lodash";
import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import { PropTypes } from "prop-types";
import { Container, Header, Segment } from "semantic-ui-react";
import { Formik, Form } from "formik";

import * as Yup from "yup";
import * as Schemas from "../forms/validationSchemas.js";

import DebouncedFormikEffect from "../utils/DebouncedFormikEffect.jsx";
import SegmentMessage from "../forms/messages/SegmentMessage.jsx";
import FormikActionsPanel from "../forms/formik/FormikActionsPanel.jsx";
import ResourceDetailsView from "../resources/resourceDetailsPanel/ResourceDetailsView.jsx";
import ImageEstimate from "./ImageEstimate.jsx";
import FormikDetailsPanel from "../forms/formik/FormikDetailsPanel/FormikDetailsPanel.jsx";

import { IMAGES } from "../../state/actions/actionConstants.js";
import { MODES } from "../forms/formConstants.js";

import estimateSelector from "../../state/reducers/estimates/estimateSelector.js";
import { getImagesEstimate } from "../../state/reducers/projectWizard/projectWizardActions.js";
import { selectCopy } from "../../state/reducers/locale/copySelector.js";

class CreateImageForm extends Component {
  static propTypes = {
    data: PropTypes.object,
    onCancel: PropTypes.func.isRequired,
    onReset: PropTypes.func,
    onSubmit: PropTypes.func.isRequired,
    copyKey: PropTypes.string.isRequired,

    estimateIsAvailable: PropTypes.bool,
    estimates: PropTypes.array,
    estimatesIsFetching: PropTypes.bool.isRequired,
    copy: PropTypes.func.isRequired
  };

  static defaultProps = {
    copyKey: "image"
  };

  constructor(props) {
    super(props);

    this.state = {
      initialValues: {
        name: "",
        description: ""
      }
    };
  }

  // -- VALIDATION -- //

  validationScheme = () => {
    const messages = this.props.copy("forms.validation_requirements");

    return Yup.object().shape({
      name: Schemas.simpleStringSchema("Name", messages, true),
      description: Yup.string()
    });
  };

  // -- HANDLERS -- //

  doEstimate = (prevFormikProps, formikProps) => {
    const { data, updateEstimate } = this.props;

    if (formikProps.values.ignoreChangeInSideEffect === true) {
      return null;
    }

    const requestData = pick(data, ["volumeSize", "service"]);
    requestData["@id"] = "create";
    requestData["@Type"] = `${data["@type"]}GoldenImage`;
    requestData.name = formikProps.values.name;
    requestData.description = formikProps.values.description;
    requestData.regions = [data.region["@id"]];
    requestData.source = data["@id"];
    requestData.operatingSystem = data.image.operatingSystem;
    requestData.licenseType = data.image.licenseType;

    updateEstimate(requestData);
  };

  onFormikSubmit = values => {
    this.props.onSubmit(values);
  };

  // -- RENDER -- //

  render() {
    const {
      data,
      mode,
      onCancel,
      onReset,
      violationErrors,
      estimateIsAvailable,
      estimates,
      estimatesIsFetching,
      copyKey,
      copy
    } = this.props;

    const readOnly = mode === MODES.READ;
    const disabled = readOnly;

    return (
      <Formik
        initialValues={this.state.initialValues} // required by semantic-ui react
        validateOnMount={true}
        enableReinitialize={true}
        validationSchema={this.validationScheme()}
        onSubmit={this.onFormikSubmit}
      >
        {formikProps => {
          return (
            <Container className="main-responsive">
              <Header as="h3" className="clear-both">
                {copy(`${copyKey}.create_image_title`)}
              </Header>

              <Form className="ui form clear-both">
                {estimateIsAvailable && (
                  <DebouncedFormikEffect onChange={this.doEstimate} />
                )}
                {get(formikProps.values, "requestAction.type", null) ===
                  IMAGES.POST && (
                  <SegmentMessage
                    title={copy(`${copyKey}.messages.created.title`)}
                    message={copy(`${copyKey}.messages.created.message`)}
                    dismissable={true}
                    positive={true}
                  />
                )}

                <Segment className="clear-both">
                  <Header as="h3" className="clear-both">
                    {copy(`${copyKey}.details_panel.details`)}
                  </Header>

                  <FormikDetailsPanel
                    readOnly={readOnly}
                    disabled={disabled}
                    apiErrors={violationErrors}
                  />

                  {estimateIsAvailable && (
                    <ImageEstimate
                      estimate={estimates[0]}
                      isFetching={estimatesIsFetching}
                      copyKey={copyKey}
                      copy={copy}
                    />
                  )}
                </Segment>

                {data && (
                  <Fragment>
                    <Segment className="clear-both">
                      <Header as="h3" floated="left">
                        {copy(`${copyKey}.create_source_title`)}
                      </Header>
                      <ResourceDetailsView data={data} />
                    </Segment>
                    <SegmentMessage
                      message={copy(`${copyKey}.messages.source_info.message`)}
                      info={true}
                    />
                  </Fragment>
                )}

                <FormikActionsPanel
                  onCancel={onCancel}
                  reset={true}
                  onReset={onReset}
                  submit={true}
                  useSubmitModal={false}
                  modalCopy={copy(`${copyKey}.modals.create`)}
                  panelCopy={copy(`${copyKey}.actions_panel.create`)}
                />
              </Form>
            </Container>
          );
        }}
      </Formik>
    );
  }
}

const mapStateToProps = (state, props) => ({
  estimateIsAvailable: estimateSelector.selectEstimateIsAvailabile(
    state,
    props
  ),
  estimates: estimateSelector.selectEstimatesByAtIDs(["create"])(state, props),
  estimatesIsFetching: state.estimates.isFetching,
  copy: selectCopy(state)
});

const mapDispatchToProps = dispatch => ({
  updateEstimate: data => dispatch(getImagesEstimate(data))
});

export default connect(mapStateToProps, mapDispatchToProps)(CreateImageForm);
